import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { DataGrid, type Column } from '../components/ui/DataGrid';
import { useToast } from '../components/ui/Toast';
import { PageHeader } from '../components/ui/PageHeader';
import { formatDate, formatEUR } from '../lib/format';
import { enumLabel } from '../lib/enumLabel';

interface OrderRow {
  id: string;
  orderNumber?: string;
  order_number?: string;
  status: string;
  total_ttc?: string | number;
  totalTTC?: number;
  customer_id?: string | null;
  created_at?: string;
  /** Settlement state derived from the payment ledger (never the fulfilment status). */
  payment_status?: string;
  amount_due?: number;
  /** 2026-10-30 — validation refused: the supplier did not reserve every unit (migration 167). */
  supplier_blocked_at?: string | null;
  supplier_block_reason?: string | null;
  /** RECAP = the account pays the monthly invoice, not the order (feedback 3.6). */
  billing?: 'RECAP' | 'DIRECT';
  /** The recap invoice(s) this order's deliveries landed on, with the invoice's own state. */
  recap_invoices?: RecapInvoiceRef[];
}

export interface RecapInvoiceRef {
  number: string;
  dueDate: string | null;
  state: 'PAID' | 'OVERDUE' | 'OPEN' | 'DISPUTE';
  lateDays: number;
}

interface OrdersListProps {
  /** Restrict to one sales channel — e.g. 'POS' for the POS Tickets screen. */
  orderType?: 'POS' | 'B2B';
  /** i18n keys under `orders.list.*`, so a caller re-titles the screen without hardcoding copy. */
  titleKey?: string;
  descriptionKey?: string;
}

/** Screen 21 — Orders list (projected per role on the backend). Reused for the
 *  POS Tickets screen by passing orderType='POS'. */
export default function OrdersList({ orderType, titleKey, descriptionKey }: OrdersListProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showApiError, push } = useToast();
  const [rows, setRows] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  // Held until the return-from-checkout handler below has had its say, so the list is not
  // fetched while the order it is about to show is still being marked paid.
  const [returnSettled, setReturnSettled] = useState(false);

  /**
   * §25 #1b — return from the hosted checkout page (?paid=1 / ?canceled=1).
   *
   * Coming back here is NOT proof of payment, and the toast alone used to be the whole handler:
   * it told the customer "paid" while the order's real status depended entirely on a webhook
   * that may still be queued, retried, or lost. CAWL's own guidance is to implement both halves,
   * so the return asks our server to ask the PSP (`POST /orders/:id/payment/sync`) and only then
   * renders the outcome. The call is idempotent and converges with the webhook on the same
   * completion path, so whichever arrives first wins and the other is a no-op.
   *
   * And the ANSWER is what we render, not the query string. CAWL sends the customer back to the
   * same return URL whether the card was accepted or declined — their docs are explicit that it
   * should be treated as a status-neutral landing page — so `?paid=1` means "they came back",
   * never "they paid". Three outcomes now: confirmed, refused, and still-pending (a webhook we
   * haven't received yet), each said plainly instead of a blanket "payment received".
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paid = params.get('paid') === '1';
    const canceled = params.get('canceled') === '1';
    const orderId = params.get('order');
    if (params.has('paid') || params.has('canceled')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (!paid && !canceled) {
      setReturnSettled(true);
      return;
    }

    let live = true;
    (async () => {
      if (canceled) {
        push({
          tone: 'warning',
          title: t('orders.list.canceledTitle'),
          message: t('orders.list.canceledMessage'),
        });
      } else {
        // 'SUCCEEDED' | 'FAILED' | 'PENDING' | 'NO_INTENT' | 'UNSUPPORTED'. Unknown when there is
        // no orderId (an operator-configured PAYMENT_SUCCESS_URL that dropped it) or when the
        // call itself fails — neither is evidence either way, so those keep the neutral wording.
        let status: string | null = null;
        if (orderId) {
          try {
            const { data } = await apiClient.post<{ status: string }>(
              `/orders/${orderId}/payment/sync`,
              {}
            );
            status = data?.status ?? null;
          } catch {
            // A sync failure is not a payment failure — the webhook is still the backstop, and
            // the row's own payment pill is the honest answer either way.
          }
        }
        if (status === 'FAILED') {
          push({
            tone: 'error',
            title: t('orders.list.paymentFailedTitle'),
            message: t('orders.list.paymentFailedMessage'),
          });
        } else if (status === 'PENDING') {
          // The card may yet be accepted: this is a "we don't know yet", not a refusal. Saying
          // "paid" here is how a customer ends up believing an unpaid order is settled.
          push({
            tone: 'warning',
            title: t('orders.list.paymentPendingTitle'),
            message: t('orders.list.paymentPendingMessage'),
          });
        } else {
          push({
            tone: 'success',
            title: t('orders.list.paidTitle'),
            message: t('orders.list.paidMessage'),
          });
        }
      }
      if (live) setReturnSettled(true);
    })();
    return () => {
      live = false;
    };
  }, [push, t]);

  useEffect(() => {
    if (!returnSettled) return;
    (async () => {
      try {
        // V1 — light list endpoint reuses /orders. Backend pagination is wired but the
        // existing endpoint exposes single-detail only; this section is intentionally
        // forward-compat with the spec'd GET /orders.
        const url = orderType ? `/orders?orderType=${orderType}` : '/orders';
        const { data } = await apiClient.get<OrderRow[] | { content: OrderRow[] }>(url);
        setRows(Array.isArray(data) ? data : (data.content ?? []));
      } catch (err) {
        showApiError(err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [showApiError, orderType, returnSettled]);

  const columns: Column<OrderRow>[] = [
    {
      key: 'orderNumber',
      header: t('orders.list.number'),
      render: (r) => r.orderNumber ?? r.order_number ?? r.id.slice(0, 8),
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (r) =>
        r.supplier_blocked_at ? (
          <span
            className="text-xs px-2 py-0.5 rounded bg-destructive/10 text-destructive"
            title={r.supplier_block_reason ?? undefined}
          >
            {t('orders.list.supplierBlocked')}
          </span>
        ) : (
          <StatusPill s={r.status} />
        ),
    },
    // Settlement is shown next to fulfilment so an operator can spot a delivered-but-unpaid
    // order at a glance instead of opening each one.
    {
      key: 'payment_status',
      header: t('orders.list.payment'),
      // The CSV carries the same answer as the cell, not the ledger column (feedback 3.6).
      csv: (r) =>
        r.billing !== 'RECAP'
          ? `${r.payment_status ?? ''}${Number(r.amount_due ?? 0) > 0 ? ` ${r.amount_due}` : ''}`
          : (r.recap_invoices ?? []).length > 0
            ? (r.recap_invoices ?? [])
                .map((i) => `${i.number} ${i.dueDate ?? ''} ${i.state}`)
                .join(' | ')
            : r.status === 'CANCELLED'
              ? ''
              : 'NOT_INVOICED',
      render: (r) =>
        r.billing === 'RECAP' ? (
          <RecapBillingCell
            status={r.status}
            totalTTC={Number(r.totalTTC ?? r.total_ttc ?? 0)}
            invoices={r.recap_invoices ?? []}
          />
        ) : (
          <PaymentPill status={r.payment_status} amountDue={r.amount_due} />
        ),
    },
    {
      key: 'totalTTC',
      header: t('common.totalTTC'),
      render: (r) => formatEUR(Number(r.totalTTC ?? r.total_ttc ?? 0)),
    },
    { key: 'created_at', header: t('orders.list.created') },
  ];

  return (
    <div>
      <PageHeader
        title={t(`orders.list.${titleKey ?? 'title'}`)}
        description={t(`orders.list.${descriptionKey ?? 'description'}`)}
      />
      <DataGrid<OrderRow>
        columns={columns}
        rows={rows}
        loading={loading}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/orders/${r.id}`)}
      />
    </div>
  );
}

/**
 * Settlement badge. `paymentStatus` is derived server-side from the payment ledger vs the order
 * total — it is NOT the fulfilment status (those were previously the same field, so a delivered
 * unpaid order looked identical to a paid one). Shows what is still owed when partly paid.
 */
export function PaymentPill({ status, amountDue }: { status?: string; amountDue?: number }) {
  const { t } = useTranslation();
  if (!status) return <span className="text-xs text-muted-foreground">—</span>;
  const cls =
    (
      {
        PAID: 'bg-success/20 text-success',
        PARTIAL: 'bg-warning/20 text-warning',
        UNPAID: 'bg-destructive/10 text-destructive',
      } as Record<string, string>
    )[status] ?? 'bg-muted text-foreground';
  const due = Number(amountDue ?? 0);
  return (
    <span className={`text-xs px-2 py-0.5 rounded ${cls}`}>
      {enumLabel('paymentStatus', status)}
      {status !== 'PAID' && due > 0 ? ` · ${t('orders.list.due', { amount: formatEUR(due) })}` : ''}
    </span>
  );
}

/**
 * Feedback 3.6 — a recap-billed account pays the MONTHLY INVOICE, never the order. Before the
 * recap exists nothing is due, so the cell shows the amount and "not invoiced yet" (nothing at
 * all on a cancelled order). Once the order's deliveries are on a recap, the cell shows that
 * invoice: its number, its due date and whether it is paid or overdue — the invoice's own state,
 * from the collection module, which is what this customer actually owes.
 */
export function RecapBillingCell({
  status,
  totalTTC,
  invoices,
}: {
  status: string;
  totalTTC: number;
  invoices: RecapInvoiceRef[];
}) {
  const { t } = useTranslation();
  // The invoice first: an order delivered in part and cancelled for the rest is still on a recap.
  if (invoices.length === 0) {
    if (status === 'CANCELLED') return <span className="text-xs text-muted-foreground">—</span>;
    return (
      <span className="text-xs text-muted-foreground">
        {formatEUR(totalTTC)} · {t('orders.list.notInvoiced')}
      </span>
    );
  }
  const cls: Record<RecapInvoiceRef['state'], string> = {
    PAID: 'bg-success/20 text-success',
    OVERDUE: 'bg-destructive/10 text-destructive',
    OPEN: 'bg-muted text-foreground',
    DISPUTE: 'bg-warning/20 text-warning',
  };
  return (
    <span className="flex flex-col gap-0.5">
      {invoices.map((i) => (
        <span key={i.number} className={`text-xs px-2 py-0.5 rounded ${cls[i.state]}`}>
          {i.number}
          {i.dueDate ? ` · ${t('orders.list.invoiceDue', { date: formatDate(i.dueDate) })}` : ''}
          {' · '}
          {i.state === 'OVERDUE'
            ? t('orders.list.invoiceOverdue', { count: i.lateDays })
            : t(`orders.list.invoiceState.${i.state}`)}
        </span>
      ))}
    </span>
  );
}

export function StatusPill({ s }: { s: string }) {
  const cls =
    (
      {
        CREATED: 'bg-muted text-foreground',
        PENDING_APPROVAL: 'bg-warning/20 text-warning',
        CONFIRMED: 'bg-blue-100 text-blue-800',
        PREPARING: 'bg-indigo-100 text-indigo-800',
        READY: 'bg-success/15 text-success',
        PARTIALLY_DELIVERED: 'bg-success/10 text-success',
        DELIVERED: 'bg-success/20 text-success',
        CANCELLED: 'bg-destructive/10 text-destructive',
      } as Record<string, string>
    )[s] ?? 'bg-muted text-foreground';
  return (
    <span className={`text-xs px-2 py-0.5 rounded ${cls}`}>{enumLabel('orderStatus', s)}</span>
  );
}
